import { makeStyles } from 'tss-react/mui'
import React, { CSSProperties, FC } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { ElementContent } from 'react-markdown/lib/ast-to-react'
import { colors } from '@hazadapt-git/public-core-base'

interface MarkdownProps {
    containerClassName?: string
    className?: string
    onLinkPress?(url: string): any | Promise<any>
    content: string
    styleOverrides?: {
        p?: CSSProperties
        h1?: CSSProperties
        h2?: CSSProperties
        h3?: CSSProperties
        h4?: CSSProperties
        h5?: CSSProperties
        li?: CSSProperties
        ul?: CSSProperties
        ol?: CSSProperties
        a?: CSSProperties
        blockquote?: CSSProperties
        strong?: CSSProperties
    }
    transformLinkUri?(
        href: string,
        children: ElementContent[],
        title: string | null
    ): string
}

export const Markdown: FC<MarkdownProps> = (props: MarkdownProps) => {
    const { classes: mdClasses } = useMdStyles()

    return (
        <div className={props.containerClassName}>
            <ReactMarkdown
                className={props.className}
                transformLinkUri={props.transformLinkUri}
                rehypePlugins={[rehypeRaw]}
                components={{
                    h1: ({ node, children, ...elemProps }) => (
                        <h1
                            {...elemProps}
                            className={mdClasses.h1}
                            style={props.styleOverrides?.h1}
                            children={children}
                        />
                    ),
                    h2: ({ node, children, ...elemProps }) => (
                        <h2
                            {...elemProps}
                            className={mdClasses.h2}
                            style={props.styleOverrides?.h2}
                            children={children}
                        />
                    ),
                    h3: ({ node, children, ...elemProps }) => (
                        <h3
                            {...elemProps}
                            className={mdClasses.h3}
                            style={props.styleOverrides?.h3}
                            children={children}
                        />
                    ),
                    h4: ({ node, children, ...elemProps }) => (
                        <h4
                            {...elemProps}
                            className={mdClasses.h4}
                            style={props.styleOverrides?.h4}
                            children={children}
                        />
                    ),
                    h5: ({ node, children, ...elemProps }) => (
                        <h5
                            {...elemProps}
                            className={mdClasses.h5}
                            style={props.styleOverrides?.h5}
                            children={children}
                        />
                    ),
                    p: ({ node, ...elemProps }) => (
                        <p
                            className={mdClasses.p}
                            style={props.styleOverrides?.p}
                            {...elemProps}
                        />
                    ),
                    li: ({ node, ...elemProps }) => {
                        const { ordered, ...liProps } = elemProps
                        return (
                            <li
                                className={mdClasses.li}
                                style={props.styleOverrides?.li}
                                {...liProps}
                            />
                        )
                    },
                    ul: ({ node, ...elemProps }) => {
                        const { ordered, ...ulProps } = elemProps
                        return (
                            <ul style={props.styleOverrides?.ul} {...ulProps} />
                        )
                    },
                    ol: ({ node, ...elemProps }) => (
                        <ol style={props.styleOverrides?.ol} {...elemProps} />
                    ),
                    a: ({ node, children, ...elemProps }) => (
                        <a
                            {...elemProps}
                            onClick={(e) => {
                                if (props.onLinkPress && elemProps.href) {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    props.onLinkPress(elemProps.href)
                                    return false
                                }
                            }}
                            className={mdClasses.a}
                            style={props.styleOverrides?.a}
                            children={children}
                        />
                    ),
                    blockquote: ({ node, ...elemProps }) => (
                        <blockquote
                            className={mdClasses.blockquote}
                            style={props.styleOverrides?.blockquote}
                            {...elemProps}
                        />
                    ),
                    strong: ({ node, ...elemProps }) => (
                        <strong
                            className={mdClasses.strong}
                            style={props.styleOverrides?.strong}
                            {...elemProps}
                        />
                    ),
                }}
                children={props.content}
            />
        </div>
    )
}

export const useMdStyles = makeStyles()({
    p: {
        fontSize: '1rem',
        paddingRight: '0.625rem',
    },
    li: {
        paddingTop: '0.15625rem',
        paddingBottom: '0.15625rem',
    },
    a: {
        color: 'blue',
        textDecoration: 'underline',
        cursor: 'pointer',
    },
    blockquote: {
        borderLeft: `0.25rem ${colors.grays.SILVER} solid`,
        paddingLeft: '0.25rem',
        marginInlineStart: '0.25rem',
        marginBlockEnd: '1.5rem',
    },
    strong: {
        fontWeight: 600,
    },
    h1: {
        fontSize: '2rem',
        fontWeight: 600,
        color: colors.grays.NOIR,
        lineHeight: '2.5rem',
    },
    h2: {
        fontSize: '1.75rem',
        fontWeight: 600,
        color: colors.grays.NOIR,
        lineHeight: '2.25rem',
    },
    h3: {
        fontSize: '1.5rem',
        fontWeight: 500,
        color: colors.grays.NOIR,
        lineHeight: '1.875rem',
    },
    h4: {
        fontSize: '1.25rem',
        fontWeight: 500,
        color: colors.grays.NOIR,
        lineHeight: '1.5rem',
    },
    h5: {
        fontSize: '1rem',
        fontStyle: 'italic',
        color: colors.grays.NOIR,
        lineHeight: '1.25rem',
    },
})
